import { useMemo, useState } from 'react';

import { useAuth, useRecruiterId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { recruiterEarningsLedgerRepoFor } from '../lib/repositories/recruiters-repo.js';
import { downloadCsv } from '../lib/csv-download.js';
import {
  EarningsControls,
  csvDateStamp,
  earningsCsvLabels,
} from '../components/earnings-controls.js';
import {
  filterLedgerByRange,
  filterLedgerByStatus,
  money,
  summariseLedgerTotals,
  toEarningsStatementCsv,
  type EarningsLedgerEntry,
  type LedgerRange,
  type LedgerStatus,
} from '@kilat/shared';

import { EmptyState, PageShell, formatDate, kindLabel, tsMs } from './recruiter-home.js';

/**
 * /recruiter/earnings — full ledger view for the recruiter persona.
 *
 * Mirror of the agent /earnings page, with two recruiter-specific
 * differences:
 *   1. `basis.shareKind` (`recruiter_signup_commission` vs
 *      `recruiter_first_invoice`) surfaces in the row pill via
 *      `kindLabel` so the recruiter sees "Per-signup commission" vs
 *      "Onboarding share" rather than two opaque "Accrued" rows.
 *   2. The per-row breakdown shows downstream-agent attribution
 *      (`basis.downstreamAgentId`) plus the flat per-signup base —
 *      recruiters earn a flat per-downstream-signup, not a rate.
 */

export function RecruiterEarningsPage() {
  const { user, loading: authLoading } = useAuth();
  const recruiterId = useRecruiterId();
  const t = useI18n();
  const brand = usePlatformBrand();
  const [range, setRange] = useState<LedgerRange>('all_time');
  const [status, setStatus] = useState<LedgerStatus>('all');
  // Listener-level failure + retry. Without onError a denied/dropped read left
  // this ledger loading forever with the error branch unreachable
  // ([[feedback-no-dead-ends]]).
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const subscribe = useMemo(() => {
    if (!recruiterId) return null;
    const repo = recruiterEarningsLedgerRepoFor(recruiterId);
    return (cb: (vs: EarningsLedgerEntry[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      );
  }, [recruiterId]);
  const { data: entries, loading } = useTrackedSnapshot<EarningsLedgerEntry[]>(
    ['recruiter-earnings', recruiterId, retryNonce],
    subscribe,
  );

  if (authLoading)
    return (
      <PageShell>
        <HeroSkeleton />
      </PageShell>
    );

  if (!user) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.recruiterEarnings.signedOutBody')}
        />
      </PageShell>
    );
  }
  if (!recruiterId) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.notRecruiterTitle')}
          body={t.t('agent.recruiterEarnings.notRecruiterBody')}
          ctaLabel={t.t('agent.payouts.notRecruiterCta')}
          ctaHref={`https://${brand.domains.marketing}/become-a-recruiter`}
        />
      </PageShell>
    );
  }
  if (loadError) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.loadError.recruiterEarningsTitle')}
          body={t.t('agent.loadError.body')}
          onRetry={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
          retryLabel={t.t('agent.loadError.retry')}
        />
      </PageShell>
    );
  }
  if (loading || entries === null) {
    return (
      <PageShell>
        <HeroSkeleton />
        <ListSkeleton />
      </PageShell>
    );
  }

  if (entries.length === 0) {
    return (
      <PageShell>
        <Hero totalSen={0} accruedSen={0} entryCount={0} />
        <EmptyState
          title={t.t('agent.earnings.emptyTitle')}
          body={t.t('agent.recruiterEarnings.emptyBody')}
        />
      </PageShell>
    );
  }

  const nowMs = Date.now();
  const filtered = filterLedgerByStatus(filterLedgerByRange(entries, range, nowMs), status);
  const totals = summariseLedgerTotals(filtered);
  return (
    <PageShell>
      <Hero totalSen={totals.net} accruedSen={totals.accrued} entryCount={filtered.length} />
      <EarningsControls
        range={range}
        status={status}
        onRange={setRange}
        onStatus={setStatus}
        onExport={() =>
          downloadCsv(
            `kilat-recruiter-earnings-${csvDateStamp(nowMs)}.csv`,
            toEarningsStatementCsv(filtered, earningsCsvLabels(t)),
          )
        }
        exportDisabled={filtered.length === 0}
      />
      {filtered.length === 0 ? (
        <EmptyState
          title={t.t('agent.earnings.filteredEmptyTitle')}
          body={t.t('agent.earnings.filteredEmptyBody')}
        />
      ) : (
        <LedgerGroups entries={filtered} />
      )}
    </PageShell>
  );
}

// ── components ─────────────────────────────────────────────────────────

function Hero({
  totalSen,
  accruedSen,
  entryCount,
}: {
  readonly totalSen: number;
  readonly accruedSen: number;
  readonly entryCount: number;
}) {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'var(--color-primary)',
        color: 'var(--color-primary-fg)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-2)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.8,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {t.t('agent.earnings.recruiterNetEarnings')}
      </p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-display)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {money.format(totalSen)}
      </p>
      <div
        style={{
          marginTop: 'var(--space-4)',
          display: 'flex',
          gap: 'var(--space-6)',
          flexWrap: 'wrap',
          fontSize: 'var(--text-meta)',
          opacity: 0.92,
        }}
      >
        <span>
          {t.t('agent.earnings.accrued')}{' '}
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{money.format(accruedSen)}</strong>
        </span>
        <span>
          {t.t('agent.earnings.entries')} <strong>{entryCount}</strong>
        </span>
      </div>
    </section>
  );
}

function HeroSkeleton() {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        height: 168,
      }}
      aria-busy="true"
      aria-label={t.t('agent.recruiterEarnings.loadingAria')}
    />
  );
}

function LedgerGroups({ entries }: { readonly entries: readonly EarningsLedgerEntry[] }) {
  const groups = useMemo(() => groupByMonth(entries), [entries]);
  return (
    <div
      className="ag-grid"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
    >
      {groups.map((g) => (
        <section
          key={g.monthKey}
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 'var(--space-3)',
            }}
          >
            <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{g.label}</h2>
            <span
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {money.format(g.subtotalSen)} · {g.entries.length}
            </span>
          </div>
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {g.entries.map((e) => (
              <LedgerRow key={e.id} entry={e} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function LedgerRow({ entry }: { readonly entry: EarningsLedgerEntry }) {
  const basis = entry.basis as Record<string, unknown>;
  const downstreamAgentId =
    typeof basis['downstreamAgentId'] === 'string' ? (basis['downstreamAgentId'] as string) : null;
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--tint-subtle)',
        alignItems: 'start',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <KindPill label={kindLabel(entry.kind, entry.basis)} kind={entry.kind} />
          <span
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {downstreamAgentId
              ? `via agent ${shortId(downstreamAgentId)}`
              : `tenant ${entry.tenantId}`}
          </span>
        </div>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {entry.invoiceId ? `Invoice ${entry.invoiceId} · ` : ''}
          {formatDate(tsMs(entry.accruedAt))}
        </p>
        <BreakdownLine entry={entry} />
      </div>
      <div
        style={{
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: entry.amount < 0 ? '#b45309' : 'var(--color-on-surface)',
        }}
      >
        {entry.amount > 0 ? '+' : ''}
        {money.format(entry.amount)}
      </div>
    </li>
  );
}

function BreakdownLine({ entry }: { readonly entry: EarningsLedgerEntry }) {
  const basis = entry.basis as Record<string, unknown>;
  const parts: string[] = [];
  // Sprint 95 — recruiter per-signup commission carries `basePerSignupSen`
  // (the flat amount before tier multiplier).
  if (typeof basis['basePerSignupSen'] === 'number' && basis['basePerSignupSen'] > 0) {
    parts.push(`Per-signup base ${money.format(basis['basePerSignupSen'] as number)}`);
  }
  // First-invoice share: shows the signup + activation split (if configured)
  const perKind = basis['perKindSen'] as Record<string, unknown> | undefined;
  if (perKind) {
    const sf = numOrZero(perKind['signup_fee']);
    const af = numOrZero(perKind['activation_fee']);
    if (sf > 0) parts.push(`Signup ${money.format(sf)}`);
    if (af > 0) parts.push(`Activation ${money.format(af)}`);
  }
  if (entry.multiplier !== 1) {
    parts.push(`×${entry.multiplier.toFixed(2)} tier`);
  }
  if (parts.length === 0) return null;
  return (
    <p
      style={{
        margin: 'var(--space-1) 0 0 0',
        fontSize: 'var(--text-meta)',
        color: 'var(--color-on-surface-muted)',
      }}
    >
      {parts.join(' · ')}
    </p>
  );
}

function KindPill({
  label,
  kind,
}: {
  readonly label: string;
  readonly kind: EarningsLedgerEntry['kind'];
}) {
  const palette = (() => {
    switch (kind) {
      case 'accrued':
        return { bg: 'rgba(13,122,79,0.12)', fg: 'var(--color-primary)' };
      case 'paid':
        return { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)' };
      case 'clawed_back':
        return { bg: 'rgba(239,68,68,0.12)', fg: '#b45309' };
      case 'adjustment':
        return { bg: 'rgba(59,130,246,0.12)', fg: '#1d4ed8' };
    }
  })();
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: palette.bg,
        color: palette.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      {label}
    </span>
  );
}

function ListSkeleton() {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      aria-busy="true"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 76,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

interface MonthGroup {
  readonly monthKey: string;
  readonly label: string;
  readonly entries: EarningsLedgerEntry[];
  readonly subtotalSen: number;
}

function groupByMonth(entries: readonly EarningsLedgerEntry[]): MonthGroup[] {
  const buckets = new Map<string, EarningsLedgerEntry[]>();
  for (const e of entries) {
    const d = new Date(tsMs(e.accruedAt));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const arr = buckets.get(key) ?? [];
    arr.push(e);
    buckets.set(key, arr);
  }
  return Array.from(buckets.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([monthKey, group]) => {
      group.sort((a, b) => tsMs(b.accruedAt) - tsMs(a.accruedAt));
      const subtotalSen = group.reduce((acc, e) => acc + e.amount, 0);
      const first = group[0];
      const sample = first ? new Date(tsMs(first.accruedAt)) : new Date();
      const label = sample.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      return { monthKey, label, entries: group, subtotalSen };
    });
}

function numOrZero(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function shortId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}
